import { t } from '../../services/i18nService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import questionSelectionService from '../../services/QuestionSelectionService';
import uiService from '../../services/UIService';
import responseReader from '../../store/readHelpers/ResponseReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';

const deliveringSubHeaderSelector = {
    selectHeaderForAutoDeliver(noAttemptedQuestions, timeHasExpired, isOnline, noUnAttemptedMandatoryQuestions) {
        if (timeHasExpired && noUnAttemptedMandatoryQuestions > 0) {
            return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredUnAttemptedMandatoryQuestions');
        }

        if (noUnAttemptedMandatoryQuestions > 0) {
            return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredUnAttemptedMandatoryQuestions');
        }
        if (noAttemptedQuestions) {
            return t('delivering.deliverPageHeaderAutoDeliverUnanswered');
        }

        if (timeHasExpired) {
            if (!isOnline) {
                return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredOffline');
            }
            return t('delivering.deliverPageHeaderAutoDeliverTimeExpired');
        }

        if (!isOnline) {
            return t('delivering.deliverPageHeaderAutoDeliverOffline');
        }

        return t('delivering.deliverPageHeaderAutoDeliver');
    },

    selectHeaderForAutoDeliverWithManual(noAttemptedQuestions, timeHasExpired, isOnline, noUnAttemptedMandatoryQuestions) {
        if (timeHasExpired) {
            if (noUnAttemptedMandatoryQuestions > 0) {
                return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredUnAttemptedMandatoryQuestions');
            }

            if (noAttemptedQuestions) {
                return t('delivering.deliverPageHeaderAutoDeliverWithManualTimeExpiredUnanswered');
            }

            if (!isOnline) {
                return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredOffline');
            }

            return t('delivering.deliverPageHeaderAutoDeliverWithManualTimeExpired');
        }

        if (noUnAttemptedMandatoryQuestions > 0) {
            return t('delivering.deliverPageHeaderAutoDeliverTimeExpiredUnAttemptedMandatoryQuestions');
        }

        if (noAttemptedQuestions) {
            return t('delivering.deliverPageHeaderAutoDeliverWithManualUnanswered');
        }

        if (!isOnline) {
            return t('delivering.deliverPageHeaderAutoDeliverOffline');
        }

        return t('delivering.deliverPageHeaderAutoDeliverWithManual');
    },

    selectDefaultHeaderSubCaption(
        unAttemptedQuestions,
        numberOfQuestions,
        autoDeliveringEnabled,
        timeHasExpired,
        noUnAttemptedMandatoryQuestions
    ) {
        if (autoDeliveringEnabled && timeHasExpired) {
            return this.getExpiredCaption(
                unAttemptedQuestions,
                numberOfQuestions,
                noUnAttemptedMandatoryQuestions
            );
        }

        if (unAttemptedQuestions) {
            return this.getIncompleteCaption(unAttemptedQuestions);
        }

        return t('delivering.statusCompleted');
    },

    getExpiredCaption(unAttemptedQuestions, numberOfQuestions, noUnAttemptedMandatoryQuestions) {
        if (unAttemptedQuestions && noUnAttemptedMandatoryQuestions > 0) {
            return t('delivering.statusTimeExpired');
        }
        return unAttemptedQuestions === numberOfQuestions
            ? t('delivering.statusTimeExpiredUnanswered')
            : t('delivering.statusTimeExpired');
    },

    getIncompleteCaption(unAttemptedQuestions) {
        const sectionManualSelectionEnabled =
        marketplacePropertiesService.isSectionManualSelectionV2Enabled();

        if (sectionManualSelectionEnabled &&
        questionSelectionService.areAllSectionsWithQuestionSelectionEnabled()) {
            return t('delivering.statusCandidateSelectionIncomplete', { number: unAttemptedQuestions });
        }

        if (questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled()) {
            return sectionManualSelectionEnabled
                ? t('delivering.statusCandidateOnlySelected')
                : t('delivering.statusCandidateSelectionIncomplete', { number: unAttemptedQuestions });
        }

        return t('delivering.statusIncomplete', { number: unAttemptedQuestions });
    },

    selectDefaultUnattemptedSubCaption(unAttemptedQuestions) {
        if (!marketplacePropertiesService.isSectionManualSelectionV2Enabled()) {
            return null;
        }

        const mixedQuestionSelectionTypes = questionSelectionService.hasMixedQuestionSelectionTypeSections();
        if (!mixedQuestionSelectionTypes || !unAttemptedQuestions) {
            return null;
        }

        return t('delivering.statusCandidateTotalUnattempted', {
            total: unAttemptedQuestions,
            selfSelected: questionSelectionService.getMissedSelectedQuestionAmount(),
            preSelected: questionSelectionService.getUnattemptedPreselectedQuestions().length,
        });
    },

    selectDefaultHeaderCaption(noAttemptedQuestions, autoDeliveringEnabled, isOnline, timeHasExpired, noUnAttemptedMandatoryQuestions) {
        if (autoDeliveringEnabled) {
            return sessionReader.isAutoDeliveringWithManualEnabled()
                ? this.selectHeaderForAutoDeliverWithManual(noAttemptedQuestions, timeHasExpired, isOnline, noUnAttemptedMandatoryQuestions)
                : this.selectHeaderForAutoDeliver(noAttemptedQuestions, timeHasExpired, isOnline, noUnAttemptedMandatoryQuestions);
        }

        if (sessionReader.userHasLockDown() && noUnAttemptedMandatoryQuestions > 0) {
            return t('delivering.deliverPageHeaderLockdown');
        }

        return sessionReader.isPublicExam()
            ? ''
            : t('delivering.deliverPageHeader');
    },

    selectSimplifiedHeaderCaption() {
        return settingsReader.isRedirectUrlEnabled() && !!settingsReader.getRedirectUrl()
            ? t('testOverview.goToRedirectionInstructions')
            : t('testOverview.startOverTextInstructions');
    },

    selectUnAttemptedQuestions(numberOfQuestions) {
        const numberOfResponses = responseReader.getTotalNumberOfResponses();
        const totalSelectedQuestionsToPull = questionSelectionService.getTotalSelectionQuestionsToPull();
        const notExcludedAttemptedQuestions = questionSelectionService.getNotExcludedAttemptedSelectionQuestions().length;
        const allSelectionQuestions = questionSelectionService.getAllSelectionQuestions().length;
        const attemptedSelectionQuestions = questionSelectionService.getAttemptedSelectionQuestions().length;

        const unAttemptedQuestions = (numberOfQuestions + attemptedSelectionQuestions + totalSelectedQuestionsToPull)
            - (allSelectionQuestions + numberOfResponses + notExcludedAttemptedQuestions);

        return unAttemptedQuestions;
    },

    selectUnAttemptedMandatoryQuestion(mandatoryQuestions) {
        return mandatoryQuestions
            .map((question) => {
                const attempted = responseReader.isQuestionDone(question);
                return { question, attempted };
            })
            .filter(item => item.attempted === false);
    },

    selectProps(ownProps) {
        const {
            numberOfQuestions,
            timeHasExpired,
            apiState: isOnline,
        } = ownProps;

        const unAttemptedQuestions = this.selectUnAttemptedQuestions(numberOfQuestions);
        const noAttemptedQuestions = unAttemptedQuestions === numberOfQuestions;
        const autoDeliveringEnabled = sessionReader.isAutoDeliveringEnabled();
        const mandatoryQuestions = questionSelectionService.getMandatoryQuestions();
        const noUnAttemptedMandatoryQuestions = this.selectUnAttemptedMandatoryQuestion(mandatoryQuestions).length;
        const defaultHeaderSubCaption = this.selectDefaultHeaderSubCaption(unAttemptedQuestions, numberOfQuestions,
            autoDeliveringEnabled, timeHasExpired, noUnAttemptedMandatoryQuestions);

        return {
            defaultHeaderCaption: this.selectDefaultHeaderCaption(noAttemptedQuestions, autoDeliveringEnabled,
                isOnline, timeHasExpired, noUnAttemptedMandatoryQuestions),
            defaultHeaderSubCaption,
            defaultUnattemptedSubCaption: this.selectDefaultUnattemptedSubCaption(unAttemptedQuestions),
            noAttemptedQuestions,
            noUnAttemptedMandatoryQuestions,
            timeHasExpired,
            showSimplifiedVersionForPublicTests: uiService.shouldUseSimplifiedSummaryPage(),
            simplifiedHeaderCaption: this.selectSimplifiedHeaderCaption(),
        };
    },
};

export default deliveringSubHeaderSelector;
